/**
 * 站点公共埋点（ssr-web-user-center）
 * - 与 www.uxmex.com 共用 Cookie clientCode
 * - 登录态与 XtContext.loginStatus / userInfo 同步
 */

import { sanitizeBehaviorTrackerQueue } from "@/utils/behaviorQueueSanitize";
import { ensureSiteClientCode } from "@/utils/behaviorClientCode";
import { isInAppWebView, resolveBehaviorClientType } from "@/utils/resolveBehaviorClientType";
import { resolveAppWebViewBehaviorContext, setAppWebViewBehaviorContext } from "@/utils/resolveAppWebViewBehaviorContext";

const TENANT_ID = 1;

const REALTIME_EVENTS = [];

let tracker = null;
let lifecycleBound = false;
let queueSanitizerBound = false;

function resyncTrackerQueueFromStorage(instance) {
  if (!instance) return;
  instance.stop();
  instance.start();
}

function syncTrackerClientCode(instance) {
  if (!instance) return null;
  const clientCode = ensureSiteClientCode();
  instance.setClientCode(clientCode);
  return clientCode;
}

function installBehaviorQueueSanitizer(instance) {
  if (queueSanitizerBound || !instance) return;
  queueSanitizerBound = true;

  const originalFlush = instance.flush.bind(instance);
  instance.flush = async () => {
    syncTrackerClientCode(instance);
    const sanitized = sanitizeBehaviorTrackerQueue();
    if (sanitized) resyncTrackerQueueFromStorage(instance);
    try {
      await originalFlush();
    } catch (err) {
      sanitizeBehaviorTrackerQueue();
      resyncTrackerQueueFromStorage(instance);
      if (process.env.NODE_ENV !== "production") {
        console.warn("[behaviorTracker] batch rejected, resynced queue after sanitize", err.message);
      }
    }
  };

  if (typeof window !== "undefined") {
    window.setInterval(() => {
      syncTrackerClientCode(instance);
      if (sanitizeBehaviorTrackerQueue()) {
        resyncTrackerQueueFromStorage(instance);
      }
    }, 5000);
  }
}

function resolveNetworkType() {
  if (typeof navigator === "undefined") return "unknown";
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return "unknown";
  const raw = String(conn.effectiveType || conn.type || "").toLowerCase();
  if (raw.includes("wifi")) return "wifi";
  if (raw.includes("5g")) return "5g";
  if (raw.includes("4g") || raw.includes("3g") || raw.includes("2g")) return "4g";
  return "unknown";
}

function applyCommonContext(instance) {
  if (!instance || typeof navigator === "undefined") return;
  const inApp = isInAppWebView();
  instance.setCommonContext({
    locale: (navigator.language || "en").split("-")[0],
    network_type: resolveNetworkType(),
    in_app_webview: inApp,
    ...(inApp ? resolveAppWebViewBehaviorContext() : {}),
  });
}

function createTracker(clientCode) {
  // eslint-disable-next-line global-require
  const { createBehaviorTracker } = require("behavior-tracker");
  return createBehaviorTracker({
    tenantId: TENANT_ID,
    clientType: resolveBehaviorClientType(),
    clientCode,
    specUrl: "/exapi/client-behavior/spec",
    batchUrl: "/exapi/client-behavior/events",
    realtimeUrl: "/exapi/client-behavior/event",
    realtimeEvents: REALTIME_EVENTS,
    autoFetchSpec: true,
    debug: process.env.NODE_ENV !== "production",
  });
}

function bindLifecycle(instance) {
  if (lifecycleBound || typeof window === "undefined") return;
  lifecycleBound = true;
  const flush = () => {
    try {
      instance.flush();
    } catch (err) {
      // ignore
    }
  };
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

export function getBehaviorTracker() {
  if (typeof window === "undefined") return null;
  const clientCode = ensureSiteClientCode();
  if (!tracker) {
    tracker = createTracker(clientCode);
    applyCommonContext(tracker);
    tracker.start();
  } else {
    tracker.setClientCode(clientCode);
  }
  return tracker;
}

export function initBehaviorTracking() {
  ensureSiteClientCode();
  sanitizeBehaviorTrackerQueue();
  const instance = getBehaviorTracker();
  if (!instance) return null;
  syncTrackerClientCode(instance);
  installBehaviorQueueSanitizer(instance);
  resyncTrackerQueueFromStorage(instance);
  bindLifecycle(instance);
  if (typeof window !== "undefined") {
    window.setBehaviorAppContext = (context) => {
      const normalized = setAppWebViewBehaviorContext(context);
      if (normalized) updateBehaviorContext(normalized);
      return normalized;
    };
  }
  // eslint-disable-next-line global-require
  require("@/utils/clientBehavior/buttonClickAutoTrack").bindAutoButtonClickTracking();
  return instance;
}

export function syncBehaviorAuth({ loginStatus, userInfo } = {}) {
  const instance = getBehaviorTracker();
  if (!instance) return;
  const userId = userInfo && (userInfo.userId || userInfo.id);
  instance.syncAuth(loginStatus, userId);
  if (loginStatus === 1 && userInfo) {
    const kycStatus = userInfo.kycStatus || userInfo.kyc_status;
    if (kycStatus) {
      instance.setCommonContext({ kyc_status: String(kycStatus).toLowerCase() });
    }
  }
}

export function trackBehaviorEvent(eventName, payload) {
  const instance = getBehaviorTracker();
  if (!instance) return null;
  return instance.track(eventName, payload);
}

export function getTrackerSpec() {
  const instance = getBehaviorTracker();
  if (!instance) return null;
  const state = instance.getState();
  return state && state.spec ? state.spec : null;
}

export function updateBehaviorContext(context) {
  const instance = getBehaviorTracker();
  if (!instance || !context) return;
  instance.setCommonContext(context);
}

export function startFunnelSession(funnelName, meta = {}) {
  if (typeof window === "undefined") return null;
  window.__behaviorFunnelSession = {
    funnel: funnelName,
    lastStep: 0,
    startedAt: Date.now(),
    ...meta,
  };
  return window.__behaviorFunnelSession;
}

export function recordFunnelStep(step, meta = {}) {
  if (typeof window === "undefined" || !window.__behaviorFunnelSession) return null;
  window.__behaviorFunnelSession.lastStep = step;
  Object.assign(window.__behaviorFunnelSession, meta);
  return window.__behaviorFunnelSession;
}

export function getFunnelSessionState() {
  if (typeof window === "undefined") return null;
  return window.__behaviorFunnelSession || null;
}

export function endFunnelSession() {
  if (typeof window !== "undefined") {
    window.__behaviorFunnelSession = null;
  }
  const instance = getBehaviorTracker();
  if (!instance) return null;
  return instance.startNewSession();
}

export { ensureSiteClientCode } from "@/utils/behaviorClientCode";
export { setAppWebViewBehaviorContext } from "@/utils/resolveAppWebViewBehaviorContext";
